import React, { useRef } from 'react';
import useGsap from '../../motion/useGsap';
import { prefersReducedMotion } from '../../motion/tokens';

const STEPS = [
  { title: 'Scope', body: 'A written brief with the features that matter, the ones we cut, a fixed price and a date.', artifact: 'Scope doc + quote' },
  { title: 'Design', body: 'Screens for every state, on your brand, reviewed with you before a line of code.', artifact: 'Clickable design' },
  { title: 'Build', body: 'Weekly releases you can open on a real URL. Auth, payments, data, dashboards, done properly.', artifact: 'Staging URL every week' },
  { title: 'Run', body: 'Deployed on your domain with monitoring, backups and mail. We keep it up and keep iterating.', artifact: 'Live product + hub access' },
];

// The one sequence on the site that earns numbering: a timeline whose
// connector draws as you scroll past each step.
export default function Process() {
  const ref = useRef(null);
  useGsap(ref, (gsap) => {
    if (prefersReducedMotion()) return;
    const line = ref.current.querySelector('[data-line]');
    const len = line.getTotalLength();
    gsap.set(line, { strokeDasharray: len, strokeDashoffset: len });
    gsap.to(line, { strokeDashoffset: 0, ease: 'none', scrollTrigger: { trigger: ref.current, start: 'top 60%', end: 'bottom 70%', scrub: true } });
  }, []);

  return (
    <section ref={ref} className="border-t border-line py-16 md:py-24">
      <div className="container-x grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-24">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <h2 className="reveal display-lg">Four steps. One team. No handoff.</h2>
          <p className="reveal lede mt-6" data-delay="80">You own the code, the domain and the data from day one, and you see every release on a real URL.</p>
        </div>
        <div className="relative pl-10 md:pl-14">
          <svg className="absolute left-3 top-2 h-[calc(100%-1rem)] w-2 md:left-5" viewBox="0 0 8 1000" preserveAspectRatio="none" aria-hidden="true">
            <line x1="4" y1="0" x2="4" y2="1000" stroke="rgba(255,255,255,0.1)" strokeWidth="2" />
            <line data-line x1="4" y1="0" x2="4" y2="1000" stroke="#5EA0FF" strokeWidth="2" />
          </svg>
          <ol className="space-y-14 md:space-y-20">
            {STEPS.map((s, i) => (
              <li key={s.title} className="reveal relative">
                <span className="absolute -left-10 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-ink text-xs text-sky ring-1 ring-sky md:-left-14 md:h-7 md:w-7">{i + 1}</span>
                <h3 className="font-display text-3xl font-normal tracking-tight md:text-4xl">{s.title}</h3>
                <p className="mt-3 max-w-prose text-mist">{s.body}</p>
                <p className="mt-3 text-sm text-amber">You get: {s.artifact}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
